/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import {
  useAccessToken,
  useIsLoggedIn,
  useUser,
} from '@airasia-rn/account/hooks';
import { BigPayApiService } from '../services/bigpayApiService';
import { useHomeConfig } from '../config/HomeConfigProvider';
import { useNetworkStatus } from './useNetworkStatus';

export const bigPayKeys = {
  balance: () => [QUERY_PERSIST_CACHE, 'bigpay', 'balance'] as const,
};

export function useBigpayApiService(): BigPayApiService {
  const { config } = useHomeConfig();
  return useMemo(() => {
    return new BigPayApiService(config.homeBigpayApi);
  }, [config.homeBigpayApi]);
}

export const useBigPayBalance = (caller: string = 'bigpay') => {
  const api = useBigpayApiService();
  const accessTokenQuery = useAccessToken(caller);
  const isLoggedInQuery = useIsLoggedIn();
  const userQuery = useUser(caller);
  const { isConnected } = useNetworkStatus();
  const queryKey = useMemo(() => bigPayKeys.balance(), []);

  // Only accounts with a linked BigPay id have a balance to fetch. Without this
  // gate every logged-in user hits the payment gateway just to be told 4046
  // ("BigPayId is missing"), which put avoidable load on real payment APIs.
  const hasLinkedBigPayId = Boolean(userQuery.data?.bigpayId);

  const isEnabled = Boolean(
    isLoggedInQuery.isSuccess &&
      isLoggedInQuery.isLoggedIn &&
      accessTokenQuery.isTokenValid &&
      isConnected &&
      hasLinkedBigPayId,
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!accessTokenQuery.data) {
        throw new Error('Access token not available');
      }

      try {
        const response = await api.fetchBalance(accessTokenQuery.data);
        return response;
      } catch (error: any) {
        // Handle 4046 error (unlinked account case)
        if (
          error?.response?.data?.code === 4046 ||
          error?.response?.data?.message?.includes('BigPayId is missing')
        ) {
          return {
            status: 'Success',
            message: 'Account not linked',
            balances: [],
            accountTier: null,
          };
        }
        throw error;
      }
    },

    staleTime: 10 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
    networkMode: 'offlineFirst',
    enabled: isEnabled,
  });

  const isUnlinked = Boolean(
    !isEnabled &&
      ((isLoggedInQuery.isSuccess && !isLoggedInQuery.isLoggedIn) ||
        (userQuery.isSuccess && !hasLinkedBigPayId)),
  );

  return { ...query, isUnlinked };
};
